"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeltaDocumentSchema = exports.DeltaOpSchema = exports.DELTA_BLOCK_EMBED_KEYS = exports.DeltaEmbedSchema = exports.ScheduleTableEmbedSchema = exports.SCHEDULE_SOURCES = exports.MergeFieldBlockEmbedSchema = exports.SignatureFieldEmbedSchema = exports.TableEmbedSchema = exports.CalloutEmbedSchema = exports.DeltaInlineOpSchema = exports.PageBreakEmbedSchema = exports.DocRefEmbedSchema = exports.MergeFieldEmbedSchema = exports.DeltaLineAttributesSchema = exports.DELTA_CALLOUT_TONES = exports.DELTA_ALIGNMENTS = exports.DELTA_LIST_TYPES = exports.DeltaInlineAttributesSchema = void 0;
exports.isDeltaBlockEmbed = isDeltaBlockEmbed;
exports.deltaPlainText = deltaPlainText;
exports.deltaMergeFields = deltaMergeFields;
exports.emptyDelta = emptyDelta;
exports.signatureBlockCount = signatureBlockCount;
const zod_1 = require("zod");
/**
 * THE STORED DOCUMENT, AS A QUILL DELTA.
 *
 * The document is one free-form rich-text flow — headings, lists, alignment,
 * inline marks and embeds — exactly as a word processor presents it, and the
 * Delta IS the storage. Nothing converts back to blocks on save.
 *
 * ── why the vocabulary is CLOSED ──────────────────────────────────────────
 *
 * This value reaches a PDF that a customer signs and whose bytes are hashed.
 * Quill will happily emit `color`, `background`, `font`, `size`, `script`,
 * `code-block`, `blockquote`, `indent`, `direction` and arbitrary embeds, and
 * neither renderer can draw most of those. An attribute the renderer silently
 * ignores is worse than one it rejects: the author sees their formatting in
 * the editor, it vanishes from the executed PDF, and nothing anywhere said so.
 *
 * So sanitisation lives HERE, at the schema boundary, and it is a
 * whitelist — `.strict()` on every attribute object. An unknown attribute is a
 * validation failure, not a silent drop. The editor is expected to configure
 * Quill's `formats` to the same closed set so this never actually fires; if it
 * does fire, the editor and the store have drifted and that is worth knowing.
 *
 * ── the shapes, all EMPIRICALLY VERIFIED against real Quill 2.0.3 ─────────
 * (`applications/super-admin-ui/src/__tests__/quillOpShapes.test.tsx`)
 *
 *   inline mark      { insert: 'text', attributes: { bold: true } }
 *   link             { insert: 'text', attributes: { link: 'https://…' } }
 *   inline embed     { insert: { mergeField: { name } } }
 *                    { insert: { docRef: { anchorId, text } } }
 *   LINE attribute   { insert: '\n', attributes: { header: 2 } }
 *
 * That last one is the shape that is easy to get wrong: a line attribute
 * rides on the NEWLINE THAT TERMINATES THE LINE, never on the text op. A
 * converter built on `{ insert: 'Term\n', attributes: { header: 2 } }` — the
 * obvious guess — loses every heading in a contract while the text survives,
 * which is the failure mode hardest to catch in review.
 */
/* ── inline attributes ──────────────────────────────────────────────────── */
/**
 * An http(s) URL, checked the same way `InlineRunSchema.href` is.
 *
 * `z.string().url()` alone is NOT enough: the WHATWG URL constructor it
 * delegates to accepts any scheme, `javascript:` included. The protocol check
 * is what actually excludes it, and this value ends up in an `href` in a
 * document someone signs.
 */
const httpUrl = zod_1.z.string().url().refine((v) => {
    try {
        return ['http:', 'https:'].includes(new URL(v).protocol);
    }
    catch {
        return false;
    }
}, { message: 'href must be an http(s) URL' });
/**
 * The inline marks, and nothing else. `.strict()` is the sanitisation.
 *
 * `strike` earns its place in a legal document specifically: struck text is
 * how an amendment shows a deleted term while leaving it readable, which is a
 * thing contracts genuinely do rather than a styling flourish.
 */
exports.DeltaInlineAttributesSchema = zod_1.z.object({
    bold: zod_1.z.literal(true).optional(),
    italic: zod_1.z.literal(true).optional(),
    underline: zod_1.z.literal(true).optional(),
    strike: zod_1.z.literal(true).optional(),
    link: httpUrl.optional(),
}).strict();
/* ── line attributes ────────────────────────────────────────────────────── */
exports.DELTA_LIST_TYPES = ['bullet', 'ordered'];
/**
 * `align` omits `'left'` deliberately: Quill emits no attribute at all for
 * left, so accepting it would create two encodings of the same thing and let
 * a round trip look lossy when it is not.
 */
exports.DELTA_ALIGNMENTS = ['center', 'right'];
/**
 * The three callout tones.
 *
 * DECLARED HERE, ABOVE ITS FIRST USE, AND NOT BESIDE `CalloutEmbedSchema`
 * WHERE IT READS MORE NATURALLY. `DeltaLineAttributesSchema` below calls
 * `z.enum(DELTA_CALLOUT_TONES)`, and `z.enum` evaluates its argument eagerly
 * while the module is still initialising — so a declaration further down the
 * file leaves this in its temporal dead zone and the whole module throws
 * `Cannot access 'DELTA_CALLOUT_TONES' before initialization` on import.
 * TypeScript does not flag it; only running the code does.
 */
exports.DELTA_CALLOUT_TONES = ['info', 'warning', 'danger'];
/**
 * Line attributes ride on a `\n` insert.
 *
 * `anchorId` is the replacement for the block id that cross-references used to
 * point at. A heading carries one; a `docRef` embed names one. Section numbers
 * are still DERIVED from heading level and position — the anchor exists only
 * so a reference survives the heading being renumbered or moved, which is the
 * whole reason ids were stable in the block model.
 *
 * `header` is 1|2|3 to match the three heading levels the renderers draw.
 * A `\n` with no attributes is an ordinary paragraph break; there is no
 * `paragraph` value, because a paragraph is the absence of a line attribute.
 */
exports.DeltaLineAttributesSchema = zod_1.z.object({
    header: zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(3)]).optional(),
    list: zod_1.z.enum(exports.DELTA_LIST_TYPES).optional(),
    align: zod_1.z.enum(exports.DELTA_ALIGNMENTS).optional(),
    /** Stable cross-reference target. Only meaningful on a heading line. */
    anchorId: zod_1.z.string().min(1).optional(),
    /**
     * A HIGHLIGHTED ASIDE, AS A PARAGRAPH STYLE.
     *
     * The `callout` BLOCK EMBED below still exists and still renders — 246 of
     * them are stored across the corpus — but it is an ATOMIC object, and an
     * atomic object cannot be typed inside. That made the one construct authors
     * reach for most the one construct they could not edit.
     *
     * As a line attribute a callout is just a run of consecutive lines wearing
     * a style, exactly like a list: an author types in it, splits it, and
     * toggles it off, with no modal anywhere. Consecutive lines of the SAME
     * tone render as one box (see `groupDeltaLines`).
     *
     * Both encodings are valid on purpose. Nothing migrates; a stored embed
     * keeps rendering as it always has until someone edits that document.
     */
    callout: zod_1.z.enum(exports.DELTA_CALLOUT_TONES).optional(),
}).strict().refine((a) => a.anchorId === undefined || a.header !== undefined, {
    message: 'anchorId belongs on a heading line — nothing else can be referenced by section number',
    path: ['anchorId'],
}).refine((a) => a.callout === undefined || a.header === undefined, {
    message: 'a line is a heading or a callout, not both — the two draw different boxes',
    path: ['callout'],
});
/* ── embeds ─────────────────────────────────────────────────────────────── */
/**
 * A merge variable, inline and atomic. The stored token format is unchanged
 * from the block model — `name` is the same catalog key `requiredMergeFields`
 * scans for and the same one the merge data is keyed by — so nothing about
 * merge resolution moves.
 */
exports.MergeFieldEmbedSchema = zod_1.z.object({
    mergeField: zod_1.z.object({
        name: zod_1.z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    }).strict(),
}).strict();
/**
 * A cross-reference, inline and atomic.
 *
 * `text` is the LAST KNOWN rendering, kept as the fallback for an
 * unresolvable target: a reference whose heading was deleted must still print
 * its last known words rather than vanish from a contract. Same rule the
 * block model's `refBlockId` runs carried.
 */
exports.DocRefEmbedSchema = zod_1.z.object({
    docRef: zod_1.z.object({
        anchorId: zod_1.z.string().min(1),
        text: zod_1.z.string(),
    }).strict(),
}).strict();
/** A page break — a BLOCK embed, so it stands between lines rather than inside one. */
exports.PageBreakEmbedSchema = zod_1.z.object({
    pageBreak: zod_1.z.literal(true),
}).strict();
/* ── block embeds ───────────────────────────────────────────────────────── */
/**
 * THE STRUCTURES A RUN OF PROSE CANNOT EXPRESS.
 *
 * A callout, a table, a signature panel and a labelled merge-field line are
 * not runs of text with a line attribute on them: each is a drawn object with
 * its own geometry in both renderers. Quill models exactly this as a BLOCK
 * EMBED — an atomic insert that stands between lines rather than inside one,
 * the same way `pageBreak` already does.
 *
 * They exist because the corpus needs them, not because the model wanted to
 * grow: 246 callouts, 122 tables, 46 signature panels and 63 labelled merge
 * lines across the 76 legal documents. Without them a migration to Delta would
 * have to drop a signature panel out of a contract or flatten a PCI
 * responsibility matrix into prose, and both are content loss in a document
 * somebody signs.
 *
 * `.strict()` everywhere, for the same reason it is on the attribute objects:
 * an unknown key is a drift signal, not something to silently drop.
 */
/**
 * One INLINE op — a text insert with optional marks, or an inline embed.
 *
 * Block embeds that hold prose (today, `callout`) hold it as a list of these
 * rather than as a plain string, so a `{{token}}` inside a callout becomes a
 * real `mergeField` embed and a cross-reference inside one stays a real
 * `docRef`. A plain string would have re-introduced exactly the literal-token
 * text the Delta model exists to get rid of.
 */
exports.DeltaInlineOpSchema = zod_1.z.union([
    zod_1.z.object({
        insert: zod_1.z.string(),
        attributes: exports.DeltaInlineAttributesSchema.optional(),
    }).strict(),
    zod_1.z.object({
        insert: zod_1.z.union([exports.MergeFieldEmbedSchema, exports.DocRefEmbedSchema]),
    }).strict(),
]);
/** A tinted, bordered aside. `content` is an inline op stream — see above. */
exports.CalloutEmbedSchema = zod_1.z.object({
    callout: zod_1.z.object({
        tone: zod_1.z.enum(exports.DELTA_CALLOUT_TONES),
        content: zod_1.z.array(exports.DeltaInlineOpSchema).min(1),
    }).strict(),
}).strict();
/**
 * A grid.
 *
 * Cells are PLAIN STRINGS, deliberately, and may still contain a literal
 * `{{token}}` which the renderers resolve on the way out. Neither renderer
 * draws inline marks, references or embeds inside a table cell — the block
 * model could not either — so giving cells an inline op stream would create a
 * representation the renderers cannot honour, which is the precise failure
 * this file's header warns about.
 */
exports.TableEmbedSchema = zod_1.z.object({
    table: zod_1.z.object({
        headers: zod_1.z.array(zod_1.z.string()),
        rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())),
    }).strict(),
}).strict();
/**
 * A signature panel: a rule, a caption, and the printed-name/title/date lines.
 *
 * The three `show*` flags are OPTIONAL and carry no default here, unlike the
 * block schema's. A default would be a silent content change on migration: a
 * stored block that never set `showName` renders no printed-name line today,
 * and defaulting it to `true` on the way into a Delta would add a line to a
 * contract nobody asked for.
 */
exports.SignatureFieldEmbedSchema = zod_1.z.object({
    signatureField: zod_1.z.object({
        signerRole: zod_1.z.string().min(1),
        label: zod_1.z.string().optional(),
        showName: zod_1.z.boolean().optional(),
        showTitle: zod_1.z.boolean().optional(),
        showDate: zod_1.z.boolean().optional(),
        nameLabel: zod_1.z.string().optional(),
        titleLabel: zod_1.z.string().optional(),
        dateLabel: zod_1.z.string().optional(),
    }).strict(),
}).strict();
/**
 * A merge field that occupies a LINE OF ITS OWN, with a label.
 *
 * Distinct from the inline `mergeField` embed: this is the "Agreement No.:
 * ______" line at the head of an agreement, which both renderers draw as its
 * own paragraph with the label in front. Modelling it as a paragraph
 * containing an inline embed would lose the label, which is the only thing
 * that tells a reader what the value is.
 */
exports.MergeFieldBlockEmbedSchema = zod_1.z.object({
    mergeFieldBlock: zod_1.z.object({
        name: zod_1.z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
        label: zod_1.z.string().optional(),
    }).strict(),
}).strict();
/**
 * A SCHEDULE — the repeating half of the document, in the one shape a contract
 * actually needs: "Schedule A — Equipment", one row per thing sold.
 *
 * A `{{token}}` resolves to a STRING, and a deal has N devices and N packages.
 * No number of scalar tokens can express that, which is why `equipment_summary`
 * has always flattened an order into one comma-separated line. This embed names
 * a ROW SOURCE instead, and the renderer draws one table row per row in it.
 *
 * `columns` is the author's choice of which of the source's fields to show, in
 * the order they should appear — all of them or a few. The names are the
 * provider's own key names (`model`, `quantity`, `unit_price`), which is what
 * `equipmentScheduleRows` emits, so a column can never name a cell the source
 * does not produce.
 *
 * `allowEmpty` is the deliberate opt-out from the empty rule: a schedule that
 * resolves to no rows THROWS at freeze, because an equipment agreement with an
 * empty Schedule A is a defect, not a default. Set it only for a genuinely
 * optional annexe.
 */
exports.SCHEDULE_SOURCES = ['equipment', 'packages'];
exports.ScheduleTableEmbedSchema = zod_1.z.object({
    scheduleTable: zod_1.z.object({
        source: zod_1.z.enum(exports.SCHEDULE_SOURCES),
        columns: zod_1.z.array(zod_1.z.object({
            /** The row field to print — a provider key name, e.g. `unit_price`. */
            key: zod_1.z.string().regex(/^[a-z][a-z0-9_]*$/),
            /** The column heading a reader sees. Required: an unlabelled column is a puzzle. */
            label: zod_1.z.string().min(1),
        })).min(1),
        title: zod_1.z.string().optional(),
        allowEmpty: zod_1.z.boolean().optional(),
    }).strict(),
}).strict();
exports.DeltaEmbedSchema = zod_1.z.union([
    exports.MergeFieldEmbedSchema,
    exports.DocRefEmbedSchema,
    exports.PageBreakEmbedSchema,
    exports.CalloutEmbedSchema,
    exports.TableEmbedSchema,
    exports.SignatureFieldEmbedSchema,
    exports.MergeFieldBlockEmbedSchema,
    exports.ScheduleTableEmbedSchema,
]);
/**
 * The embed keys that stand BETWEEN lines rather than inside one.
 *
 * Exported so the renderers and the editor agree on the classification
 * without either restating it: a block embed the walker treated as inline
 * would appear mid-paragraph, and an inline embed treated as a block would
 * split a sentence in half.
 */
exports.DELTA_BLOCK_EMBED_KEYS = [
    'pageBreak', 'callout', 'table', 'signatureField', 'mergeFieldBlock', 'scheduleTable',
];
function isDeltaBlockEmbed(embed) {
    return exports.DELTA_BLOCK_EMBED_KEYS.some((k) => k in embed);
}
/* ── ops and the document ───────────────────────────────────────────────── */
/**
 * One op. Text inserts may carry inline attributes; a `\n`-only insert may
 * carry line attributes; an embed carries neither.
 *
 * Text and line attributes are NOT separated into two op types here even
 * though Quill keeps them apart, because a single op can legitimately be
 * `{ insert: 'a\nb\n' }` — consecutive unattributed lines COLLAPSE into one
 * op (verified against real Quill), so "is this a line op?" is a property of
 * the attributes, not of the text. The union below accepts either attribute
 * shape on a string insert and the renderers split on `\n` across the whole
 * op stream, which is the only correct way to walk a Delta.
 */
exports.DeltaOpSchema = zod_1.z.union([
    zod_1.z.object({
        insert: zod_1.z.string(),
        attributes: zod_1.z.union([exports.DeltaInlineAttributesSchema, exports.DeltaLineAttributesSchema]).optional(),
    }).strict(),
    zod_1.z.object({
        insert: exports.DeltaEmbedSchema,
    }).strict(),
]);
/**
 * The stored document.
 *
 * MUST END WITH A NEWLINE. This is not pedantry: a line attribute rides on
 * the newline that terminates its line, so a document not ending in `\n` has
 * a final line whose heading, list marker or alignment has nowhere to live.
 * Quill maintains this invariant itself (an empty document is
 * `[{ insert: '\n' }]`, never `[]`), so anything violating it did not come
 * from the editor.
 */
exports.DeltaDocumentSchema = zod_1.z.object({
    ops: zod_1.z.array(exports.DeltaOpSchema).min(1),
}).refine((d) => {
    const last = d.ops[d.ops.length - 1];
    return typeof last?.insert === 'string' && last.insert.endsWith('\n');
}, {
    message: 'a Delta document must end with a newline — line attributes ride on the terminating \\n',
    path: ['ops'],
});
/* ── helpers ────────────────────────────────────────────────────────────── */
/**
 * The plain text of a Delta — what `requiredMergeFields`-style scanning and a
 * wording diff read, the role `DocumentBlock.text` used to play.
 *
 * An embed contributes its TOKEN, not nothing: a merge field is
 * `{{name}}` and a reference is its fallback text, so the plain text of a
 * Delta matches the plain text of the block document it was migrated from.
 * A page break contributes nothing — it has no words.
 */
function deltaPlainText(delta) {
    let out = '';
    for (const op of delta.ops) {
        if (typeof op.insert === 'string') {
            out += op.insert;
            continue;
        }
        out += embedPlainText(op.insert);
    }
    return out;
}
/**
 * The words one embed contributes.
 *
 * A block embed contributes its OWN words, not nothing: a callout's prose, a
 * table's cells and a signature panel's caption are all text a reader reads
 * and a wording diff must see. Getting this wrong would let a clause change
 * inside a callout land as "no textual change" in a version comparison.
 */
function embedPlainText(embed) {
    if ('mergeField' in embed)
        return `{{${embed.mergeField.name}}}`;
    if ('docRef' in embed)
        return embed.docRef.text;
    if ('pageBreak' in embed)
        return '';
    if ('callout' in embed) {
        return embed.callout.content.map((op) => (typeof op.insert === 'string' ? op.insert : embedPlainText(op.insert))).join('');
    }
    if ('table' in embed) {
        return [...embed.table.headers, ...embed.table.rows.flat()].join('\n');
    }
    if ('signatureField' in embed)
        return embed.signatureField.label ?? embed.signatureField.signerRole;
    if ('scheduleTable' in embed) {
        // The TITLE and HEADINGS are the schedule's own words — an author editing
        // "Unit price" to "Price each" changed the contract's wording and a version
        // diff must see it. The ROWS are not: they come from the order, not from
        // the document, and differ per envelope.
        const s = embed.scheduleTable;
        return [s.title ?? '', ...s.columns.map((c) => c.label)].filter(Boolean).join('\n');
    }
    if ('mergeFieldBlock' in embed) {
        const b = embed.mergeFieldBlock;
        return `${b.label ? `${b.label}: ` : ''}{{${b.name}}}`;
    }
    return '';
}
/** Every merge-field name the document uses, in first-appearance order and
 *  de-duplicated — the Delta equivalent of `requiredMergeFields`. */
function deltaMergeFields(delta) {
    const seen = new Set();
    const out = [];
    // Scanned over the PLAIN TEXT rather than over the embeds alone, because a
    // `{{token}}` can also sit as literal text inside a table cell or authored
    // prose. `deltaPlainText` already prints every merge embed as `{{name}}`, so
    // one pattern finds both encodings and neither can be missed — and a field
    // this misses is a field the render throws on at signing time.
    const pattern = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
    for (const m of deltaPlainText(delta).matchAll(pattern)) {
        const name = m[1];
        if (!seen.has(name)) {
            seen.add(name);
            out.push(name);
        }
    }
    return out;
}
/** An empty document, in the shape Quill itself produces. */
function emptyDelta() {
    return { ops: [{ insert: '\n' }] };
}
/**
 * HOW MANY SIGNATURE BLOCKS A VERSION CARRIES, whichever shape it stores.
 *
 * A version holds EITHER `blocks` (the original shape) or a `delta` (what the
 * continuous editor writes, and what every migrated document actually uses) —
 * and when a delta is present it is authoritative. Counting only `blocks` would
 * report zero for every real document, which is how a "does this need a
 * signature?" check silently answers no for the entire library.
 *
 * Used to decide a document's discharge when its author has not declared one,
 * and to catch the one combination that cannot render (`dischargeConflict`).
 */
function signatureBlockCount(version) {
    const delta = version?.delta;
    if (delta) {
        const ops = delta?.ops;
        if (!Array.isArray(ops))
            return 0;
        return ops.filter((op) => {
            const insert = op?.insert;
            return insert && typeof insert === 'object' && 'signatureField' in insert;
        }).length;
    }
    const blocks = version?.blocks;
    if (!Array.isArray(blocks))
        return 0;
    return blocks.filter((b) => b?.type === 'signatureField').length;
}
//# sourceMappingURL=documentDelta.js.map